// src/portal/PortalPlanHead.jsx
//
// PORTED from the mock's `.plan-head` — breadcrumb, queue position with
// prev/next, title, case number, counterparty, status chip and age.
import { Link } from "@tanstack/react-router";
import { detailCounterparty } from "./portalCounterparty";
import { statusGuidance } from "./portalStatus";

const STATUS_WORD = {
  draft: "Draft",
  submitted: "With the reviewer",
  changes_requested: "Changes requested",
  approved: "Approved",
};
const STATUS_WORD_REVIEWER = { ...STATUS_WORD, submitted: "Waiting on you" };

function ageFrom(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days <= 0) return "opened today";
  return days === 1 ? "1 day open" : `${days} days open`;
}

/**
 * @param {object} p
 * @param {object} p.review          route 6's shaped review
 * @param {string} p.role            viewer_role
 * @param {Array}  p.queue           the AWAITING-YOU queue, in list order
 */
export default function PortalPlanHead({ review, role, queue = [] }) {
  const words = role === "reviewer" ? STATUS_WORD_REVIEWER : STATUS_WORD;
  const age = ageFrom(review.created_at);
  const counterparty = detailCounterparty(review);
  const guidance = statusGuidance(role, review.status);

  // ── PREV/NEXT WALKS THE AWAITING-YOU QUEUE, NOT EVERYTHING VISIBLE ────────
  //
  // Stepping through "everything you can see" would walk approved plans nobody
  // is waiting on, which is not a queue. `queue` is already the awaiting set --
  // filtered by route 5's own derivation from the writer predicate -- so the
  // position counts the same plans the rollup card counted.
  //
  // A plan you are LOOKING at need not be in it: open an approved plan from the
  // full list and there is no position to report, so the control is absent
  // rather than showing a misleading "1 of 7".
  const idx = queue.findIndex((r) => r.id === review.id);
  const inQueue = idx !== -1;
  const prev = inQueue && idx > 0 ? queue[idx - 1] : null;
  const next = inQueue && idx < queue.length - 1 ? queue[idx + 1] : null;

  const step = (target, label) =>
    target ? (
      <Link
        to="/portal/plan-reviews/$planReviewId"
        params={{ planReviewId: target.id }}
        aria-label={label}
        className="rounded border border-bg-border px-1.5 py-0.5 text-[11px] text-fg-secondary transition hover:text-fg-primary"
      >
        {label === "Previous plan" ? "‹" : "›"}
      </Link>
    ) : (
      <span
        aria-hidden="true"
        className="rounded border border-bg-border px-1.5 py-0.5 text-[11px] text-fg-secondary opacity-30"
      >
        {label === "Previous plan" ? "‹" : "›"}
      </span>
    );

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-3">
        <nav className="flex items-center gap-1.5 text-[11px] text-fg-secondary">
          <Link to="/portal/plan-reviews" className="transition hover:text-fg-primary">Plan reviews</Link>
          <span aria-hidden="true">/</span>
          {/* ── THE CRUMB IS THE CASE NUMBER, AND THAT IS A DERIVATION ──────
              The mock's `.crumbs__here` is a SHORT FORM of the plan name
              ("Spanish Trail 14-3" against a title of "Spanish Trail 14-3 — Pad
              & Access Road"). No short name exists in the data, and putting the
              full title here renders it twice — once in the crumb and once as
              the h1 below, which is what the first attempt did.
              The case number is short, stable and unique, which is what a crumb
              wants. It therefore does NOT repeat in the meta line. */}
          <span className="truncate font-mono text-fg-primary">{review.case_number}</span>
        </nav>

        {/* ── THE GUIDANCE SITS IN THE ROW THAT ALREADY EXISTS ──────────────
            Above the chip, and costing no line of its own. This row held a
            breadcrumb on the left and the queue stepper on the right with empty
            space between them; the guidance takes that space, which drops the
            right-hand stack from three lines to two. Measured by rendering THIS
            component and HEAD's own copy of it side by side in Chromium at
            1100px, with the deployed webfonts: 95.0px -> 74.5px.

            HIDDEN BELOW md, not truncated to nothing. Crushed between a
            breadcrumb and a stepper it would be a few words and an ellipsis,
            which is worse than absent — the chip still says the state, and the
            guidance is the part you can afford to lose when there is no room. */}
        {guidance && (
          <span
            data-testid="status-guidance"
            className="hidden min-w-0 flex-1 truncate px-2 text-right text-[11px] text-fg-secondary md:block"
          >
            {guidance}
          </span>
        )}

        {inQueue && (
          <div className="flex shrink-0 items-center gap-1.5">
            <span className="font-mono text-[10px] uppercase tracking-[0.09em] text-fg-secondary">
              {idx + 1} of {queue.length} in queue
            </span>
            {step(prev, "Previous plan")}
            {step(next, "Next plan")}
          </div>
        )}
      </div>

      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="font-display text-[20px] font-semibold leading-tight text-fg-primary">
            {review.title}
          </h1>
          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-fg-secondary">
            {counterparty && <span>{counterparty}</span>}
            {review.submitter_username_at_create && role === "reviewer" && (
              <><span aria-hidden="true">·</span><span>{review.submitter_username_at_create}</span></>
            )}
          </div>
        </div>

        <div className="flex shrink-0 flex-col items-end gap-1">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-accent-action/40 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.08em] text-accent-action">
            <span className="h-1.5 w-1.5 rounded-full bg-accent-action" />
            {words[review.status] ?? review.status}
          </span>
          {age && <span className="text-[10px] text-fg-secondary">{age}</span>}
        </div>
      </div>
    </div>
  );
}
